import { useState } from 'react';

import { Spinner } from '@atoms/Spinner/Spinner';

import { TableLayout } from 'components/ui/templates/TableLayout/TableLayout';

import { AuthenticationBanner } from './AuthenticationBanner';
import { AuthenticationEmptyState } from './AuthenticationEmptyState';
import { AuthenticationFilter } from './AuthenticationFilter';
import { AuthenticationGrid } from './AuthenticationGrid';
import { AuthenticationHeader } from './AuthenticationHeader';
import {
  AUTHENTICATION_ERROR_TEST_ID,
  AUTHENTICATION_FILTERED_EMPTY_TEST_ID,
  AUTHENTICATION_LOADING_TEST_ID,
  AUTHENTICATION_PAGE_TEST_ID
} from './constants';
import { CreateKeyModal } from './CreateKeyModal';
import type { AuthenticationKeyFilter } from './types';

import { EMPTY_STATE_MIN_HEIGHT } from '../constants';
import { useAuthenticationKeys } from '../hooks/useAuthenticationKeys';

export const Authentication = () => {
  const [filter, setFilter] = useState<AuthenticationKeyFilter>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { keys, isLoading, isError } = useAuthenticationKeys(filter);

  const openCreate = () => setIsCreateOpen(true);
  const closeCreate = () => setIsCreateOpen(false);

  const showFullEmptyState =
    !isLoading && !isError && filter === 'all' && keys.length === 0;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div
          data-testid={AUTHENTICATION_LOADING_TEST_ID}
          className={`flex ${EMPTY_STATE_MIN_HEIGHT} items-center justify-center`}
        >
          <Spinner size="large" />
        </div>
      );
    }

    if (isError) {
      return (
        <div
          data-testid={AUTHENTICATION_ERROR_TEST_ID}
          className="flex items-center justify-center py-12 text-content-secondary"
        >
          Something went wrong loading your authentication keys. Please try
          again.
        </div>
      );
    }

    if (showFullEmptyState) {
      return <AuthenticationEmptyState onCreate={openCreate} />;
    }

    return (
      <div className="flex flex-col gap-6 pb-6">
        <AuthenticationBanner />
        <AuthenticationFilter value={filter} onChange={setFilter} />
        {keys.length === 0 ? (
          <div
            data-testid={AUTHENTICATION_FILTERED_EMPTY_TEST_ID}
            className="flex items-center justify-center py-12 text-content-secondary"
          >
            No {filter} authentication keys.
          </div>
        ) : (
          <AuthenticationGrid keys={keys} />
        )}
      </div>
    );
  };

  return (
    <>
      <TableLayout
        containerTestId={AUTHENTICATION_PAGE_TEST_ID}
        header={
          <AuthenticationHeader
            showCreateAction={!showFullEmptyState}
            onCreate={openCreate}
          />
        }
        content={renderContent()}
      />
      <CreateKeyModal open={isCreateOpen} onClose={closeCreate} />
    </>
  );
};
